import React, { useCallback, useEffect, useState } from 'react';
import ReusableTable, { TableColumn } from '../components/adminTable';
import Pagination from '../components/Pagination';
import { Clock } from 'lucide-react';
import {
    submissionService,
    ReviewedManuscriptForReReviewItem,
    GetReviewerAssignmentPayload,
} from '../services/adminAuth';
import { formatDateDisplay } from '../../../utils/dateHelper';

const ReviewedManuscriptsForReReview: React.FC = () => {
    const [items, setItems] = useState<ReviewedManuscriptForReReviewItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const payload: GetReviewerAssignmentPayload = {
                pageNumber: currentPage,
                pageSize,
                sortBy: '',
                sortDescending: true,
            };
            const res = await submissionService.getReviewedManuscriptForReReviewByAdmin(payload);
            if (res.isSuccess && res.data) {
                setItems(res.data.data);
                setTotalRecords(res.data.totalRecords);
                setTotalPages(res.data.totalPages);
            }
        } catch {
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const filtered = React.useMemo(() => {
        if (!search.trim()) return items;
        const q = search.toLowerCase();
        return items.filter(
            (r) =>
                r.title?.toLowerCase().includes(q) ||
                r.submissionNumber?.toLowerCase().includes(q) ||
                r.categoryName?.toLowerCase().includes(q) ||
                r.reviewerName?.toLowerCase().includes(q)
        );
    }, [items, search]);

    const columns: TableColumn<ReviewedManuscriptForReReviewItem>[] = [
        {
            key: 'submissionNumber',
            label: 'Submission ID',
            render: (value) => <span className="font-bold text-gray-900">{value}</span>,
        },
        {
            key: 'title',
            label: 'Manuscript Title',
            className: 'max-w-[300px]',
            render: (value) => (
                <div className="font-medium text-gray-700 leading-snug truncate" title={value}>
                    {value}
                </div>
            ),
        },
        {
            key: 'reviewerName',
            label: 'Reviewer',
            render: (value) => <span className="text-gray-700 text-sm">{value || '—'}</span>,
        },
        {
            key: 'categoryName',
            label: 'Thematic Area',
            render: (value) => <span className="text-gray-600 text-sm">{value}</span>,
        },
        {
            key: 'decision',
            label: 'Decision',
            render: (value) => <span className="text-gray-600 text-sm">{value || '—'}</span>,
        },
        {
            key: 'totalScore',
            label: 'Score',
            render: (value: number) => (
                <span className="text-sm font-semibold text-gray-700">{value ?? '—'}</span>
            ),
        },
        {
            key: 'reason',
            label: 'Chairman Reason',
            className: 'max-w-[260px]',
            render: (value: string) => (
                <span className="text-xs text-gray-500 italic block truncate" title={value}>
                    {value || '—'}
                </span>
            ),
        },
        {
            key: 'dateSubmitted',
            label: 'Submitted',
            render: (value: string) => (
                <span className="text-gray-400 text-xs font-medium">{formatDateDisplay(value)}</span>
            ),
        },
        {
            key: 'deadLine',
            label: 'Deadline',
            render: (value: string) => (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    <Clock className="w-3 h-3" />
                    {formatDateDisplay(value)}
                </span>
            ),
        },
    ];

    return (
        <div className="w-full">
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    Reviewed Manuscripts Returned for Re-Review
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    Manuscripts that thematic-area chairmen have returned to reviewers for re-assessment.
                </p>
            </div>

            <ReusableTable
                title="Returned Reviewed Manuscripts"
                subTitle={`${totalRecords} record${totalRecords === 1 ? '' : 's'}`}
                columns={columns}
                data={filtered}
                loading={loading}
                searchPlaceholder="Search by title, ID, thematic area, reviewer..."
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No manuscripts returned for re-review."
                showSearch
            />

            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}
        </div>
    );
};

export default ReviewedManuscriptsForReReview;
